import React, { useEffect, useMemo, useRef, useState } from "react";
import { noteAttention, clearAttention } from "../utils/chatContext";
import { Link, useLocation } from "react-router-dom";

// ---------- Helper: build an array of images in /public/projects ----------
const PU = process.env.PUBLIC_URL;

const SPIM_IMAGES = Array.from(
  { length: 5 },
  (_, i) => `${PU}/projects/SPIM_${i + 1}.jpg`
);
const POKE_IMAGES = [
  `${PU}/projects/POKE_1.jpg`, // card page — raw market index, full history
  `${PU}/projects/POKE_2.jpg`, // graded view — PSA 10 comps
  `${PU}/projects/POKE_holo.gif`, // rarity-aware 3D holo viewer in motion
  `${PU}/projects/POKE_3.jpg`, // demo collection slab wall
  `${PU}/projects/POKE_4.jpg`, // portfolio dashboard
  `${PU}/projects/POKE_5.jpg`, // market premiums
  `${PU}/projects/POKE_6.jpg`, // sealed products
];
const SIMON_IMAGES = [
  `${PU}/projects/SIMON_1.jpg`, // glazed porcelain, front
  `${PU}/projects/SIMON_4.jpg`, // porcelain, transcript open - marble q&a
  `${PU}/projects/SIMON_2.jpg`, // weathered stone
  `${PU}/projects/SIMON_3.jpg`, // rusted bronze, three-quarter
  `${PU}/projects/SIMON_5.jpg`, // two full exchanges, advice for the living
];
const JARJAR_IMAGES = Array.from(
  { length: 4 },
  (_, i) => `${PU}/projects/JARJAR_${i + 1}.jpg`
);
const SPEC_IMAGES = Array.from(
  { length: 5 },
  (_, i) => `${PU}/projects/SPEC_${i + 1}.jpg`
);
const COOK_IMAGES = Array.from(
  { length: 4 },
  (_, i) => `${PU}/projects/COOK_${i + 1}.jpg`
);
const COMIC_IMAGES = Array.from(
  { length: 6 },
  (_, i) => `${PU}/projects/COMIC_${i + 1}.jpg`
);
const TEACH_IMAGES = Array.from(
  { length: 2 },
  (_, i) => `${PU}/projects/TEACH_${i + 1}.jpg`
);
// Use the optimized ~800px webp thumbnails (generated by build-art-assets.mjs)
// rather than the multi-MB originals — this is a 280px preview carousel.
const ART_IMAGES = [
  `${PU}/art/thumbs/Painting/Hyder'sV1.webp`,
  `${PU}/art/thumbs/ScratchBoard/WolfScratchV2.webp`,
  `${PU}/art/thumbs/Hands/INVHand3V1.webp`,
];
// Full-resolution originals — loaded only when the lightbox opens (on demand),
// so the carousel stays light but the zoomed view is full quality.
const ART_FULL = [
  `${PU}/art/artworks/Painting/Hyder'sV1.JPG`,
  `${PU}/art/artworks/ScratchBoard/WolfScratchV2.jpg`,
  `${PU}/art/artworks/Hands/INVHand3V1.jpeg`,
];

// Corporate Footprints brand board (quadrants of the identity sheet) — the
// globe itself predates any surviving screenshots.
const GLOBE_IMAGES = Array.from(
  { length: 2 },
  (_, i) => `${PU}/projects/GLOBE_${i + 1}.jpg`
);

const DOT_IMAGES = Array.from(
  { length: 5 },
  (_, i) => `${PU}/projects/DOT_${i + 1}.png`
);

const CC_IMAGES = Array.from(
  { length: 9 },
  (_, i) => `${PU}/projects/CC_${i + 1}.jpg`
);


const TYPO_IMAGES = Array.from(
  { length: 3 },
  (_, i) => `${PU}/projects/TYPO_${i + 1}.jpg`
);

const GLOWPOP_IMAGES = [
  `${PU}/projects/GLOWPOP_1.jpg`, // hero
  `${PU}/projects/GLOWPOP_2.jpg`, // ingredient flip cards
  `${PU}/projects/GLOWPOP_3.jpg`, // benefits / product section
  `${PU}/projects/GLOWPOP_4.jpg`, // comparison table + 3D lollipop
  `${PU}/projects/GLOWPOP_5.jpg`, // the Real Sugar Difference
];

const GG_IMAGES = [
  `${PU}/projects/GG_1.jpg`, // hero — STRENGTH RUNS ON GREENS
  `${PU}/projects/GG_2.jpg`, // why + IN/CUT FROM THE FORMULA comparison
  `${PU}/projects/GG_3.jpg`, // the protocol — two gummies
  `${PU}/projects/GG_4.jpg`, // 15 organic superfoods + Supplement Facts
];

// Soulful Expressions — GIFs lead to showcase the interactive parts,
// interleaved with stills that walk down the page.
const SOUL_IMAGES = [
  `${PU}/projects/SOUL_hero.gif`, // Vanta birds scattering from the cursor (real-time)
  `${PU}/projects/SOUL_1.jpg`, // interactive benefit cards
  `${PU}/projects/SOUL_draw.gif`, // free-draw canvas in the "Power of Art" section
  `${PU}/projects/SOUL_2.jpg`, // "How Sessions Work" orbital
  `${PU}/projects/SOUL_3.jpg`, // group workshops
  `${PU}/projects/SOUL_4.jpg`, // therapist / about
];

// Lavoët — GIFs lead (the interactive engraved trunk, then the pattern studio
// in motion), followed by stills. LAVOET_pattern.gif is regenerable/replaceable
// via record-demo.mjs (record your own demo, same filename, no code change).
const LAVOET_IMAGES = [
  `${PU}/projects/LAVOET_trunk.gif`, // engraved trunk plate: clasps open, lid lifts
  `${PU}/projects/LAVOET_1.jpg`, // the house monogram canvas (toile)
  `${PU}/projects/LAVOET_2.jpg`, // brass signature plaque
  `${PU}/projects/LAVOET_pattern.gif`, // parametric pattern studio in motion
];
// ---------- Category filter ----------
const CATEGORIES = [
  { key: "all",      label: "All" },
  { key: "web",      label: "Web Dev" },
  { key: "software", label: "Software" },
  { key: "art",      label: "Art" },
  { key: "other",    label: "Other" },
];

// ---------- Project data ----------
/* Anchor identity for a project, shared with the chat so the head can link
   to a write-up it mentions. Derived from the title so there is nothing to
   keep in sync by hand. */
export const projectSlug = (title) =>
  title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* The name a person actually says: everything before the em-dash gloss or
   the parenthetical. "SPIM (Salavon's...)" -> "SPIM". */
export const projectShortName = (title) => title.split(/\s+[—–(]/)[0].trim();

export const projects = [
  // 1. SPIM
  {
    title: "SPIM (Salavon's Pathology Inducing Machine)",
    color: "#00ff88",
    tags: ["web", "software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 5,
      role: "solo - ~96% of the code, over two-ish years alongside my degree, for Jason Salavon's studio",
      stack: ["Python", "PyTorch", "Stable Diffusion / A1111 internals", "React", "distributed GPU servers I built and ran"],
      scale: "800+ commits; live tensor surgery on a running model; the studio's production tool, with a museum show made on it",
      fit: ["AI/ML research", "ML infrastructure", "backend", "frontend", "systems"],
      hardest: "hot-editing a diffusion model's tensors mid-generation while LoRA, ControlNet and AnimateDiff keep working",
    },
    images: SPIM_IMAGES,
    description: [
      "A research platform for understanding diffusion models from the inside: modify a model's internal tensors mid-generation and study how its output — and human perception of it — changes. Off-manifold, dreamlike imagery is the observable result, not the goal.",
      "I built effectively all of it: 800+ commits (~96% of the codebase) of Python and React over two-ish years of 20–50-hour weeks (alongside my degree), from the browser UI down to the GPU servers it ran on. The interface is still live to explore; the original GPU fleet has spun down, but I run the system locally — **happy to arrange a live demo of the investor-facing front end on request** (the in-house research tooling I built for the studio is under NDA).",
      {
        summary: "The questions: which layers hold style, structure, coherence?",
        details:
          "Which layers hold style, which hold structure, where does coherence live? Experiments intervene layer-by-layer to find out — change the tensor, watch what changes.",
      },
      {
        summary: "Live model surgery — tensors edited mid-generation, no restarts.",
        details: [
          "Modified Automatic1111's internals (sd_models.py, networks.py, processing.py) so experiments hot-edit model tensors while the pipeline is running.",
          {
            summary: "Wildcard layer targeting.",
            details:
              "Transforms address the model by pattern — a layer mask like diffusion_model.*.1.norm selects every matching layer at once, so a single weight/bias formula can rewrite dozens of tensors across the U-Net or text encoder in one experiment.",
          },
          {
            summary: "Making LoRA coexist with edited weights.",
            details:
              "A1111's LoRA path restores pristine cached weights before applying deltas — silently erasing live edits. Rewriting the apply path to compose onto the current tensors (with a zero-strength trick to un-apply) lets AnimateDiff, ControlNet and LoRA run on a modified model at once.",
          },
          {
            summary: "The tensor-“bleed” fix.",
            details:
              "Edits from one experiment persisted into the next — PyTorch tensors are shared references into the loaded model, so mutations stuck. Cloning the touched sub-portions isolated each run.",
          },
          {
            summary: "Safe-output masking.",
            details:
              "The platform deliberately destabilizes models, so filtering unsafe results is built into the pipeline rather than bolted on.",
          },
        ],
      },
      {
        summary: "A formula language expresses the experiments.",
        details: [
          "A randomized-formula engine rewrites layer weights — weighted-probability generation with automatic simplification — alongside pixel-level perturbation tools.",
          {
            summary: "A formula keyboard with its own parser.",
            details:
              "The React UI ships a purpose-built formula keyboard; entries parse into the engine's grammar, and statistical-novelty filters plus logging quantify what large runs produce.",
          },
          {
            summary: "ML prunes the search space.",
            details:
              "Lightweight text classifiers — separate models for weight and bias formulas, trained on curated good/bad corpora — predict whether a randomly generated formula is worth GPU time before it ever runs.",
          },
        ],
      },
      {
        summary: "A distributed GPU backend I built, provisioned, and ran.",
        details: [
          "An auth gatekeeper farms generation jobs out to standby render servers — a design that grew from a single box, with custom memory management. The Ubuntu GPU machines are ones I built and tuned myself.",
          {
            summary: "Preview-then-commit rendering.",
            details:
              "Cheap low-res previews pass a statistical quality check before anything earns full-resolution GPU time.",
          },
          {
            summary: "Unattended runs police themselves.",
            details:
              "Noise, monochrome, black-frame and perceptual-similarity classifiers auto-discard failed generations — stills and video — quarantine their configs, and stream a live rejection tally while batches run overnight.",
          },
        ],
      },
      {
        summary: "Museum-shown work came out of it.",
        details: [
          "The platform became the production tool of Jason Salavon's studio: his 2024 solo show “History Painting” (TAI Modern, Santa Fe) — four monumental panels and looping animations — was made with it. “A tool that forces the AI out of that universe,” as Salavon puts it.",
          {
            summary: "Print-scale and motion output.",
            details:
              "AnimateDiff, ControlNet and LoRA run simultaneously for the animations; Voronoi-based tiling assembles ultra-high-res stills; finished pieces can mint as NFTs via IPFS.",
          },
          {
            summary: "The animations interpolate the model itself.",
            details:
              "Motion comes from weight space: formula-to-formula interpolation in n steps, up to blends between two entire model states, with wildcard layer-matching keeping the keyframe configs compact.",
          },
        ],
      },
    ],
    links: [
      {
        label: "Visit Site (UI only)",
        href: "https://spim.hydermohyuddin.com/",
        note:
          "The real front end, fully explorable — its GPU servers are retired, so generate won't run until I hand you a server address. Press it there and it tells you how.",
      },
      {
        label: "Museum Show",
        href: "https://taimodern.com/exhibit/history-painting-jason-salavon/",
      },
    ],
  },
  // 2. PokeCompare
  {
    title: "PokeCompare — Pokémon TCG Price Aggregator",
    color: "#ffcb05",
    tags: ["web", "software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 5,
      role: "solo, built and run as a product, then archived as a $0-forever site",
      stack: ["Next.js 15", "TypeScript", "libSQL (Turso)", "Cloudflare R2", "GitHub Actions pipelines", "FTS5 search"],
      scale: "151k printings resolved into 65.6k cards; 4.2M price snapshots; 96k images; eight scheduled pipelines under a budget gate",
      fit: ["data engineering", "data science", "backend", "frontend", "product"],
      hardest: "card identity across six sources that never agree, and a price-prediction evaluation rebuilt leak-free after it caught my own best result",
    },
    images: POKE_IMAGES,
    description: [
      "A solo-built answer to a deceptively messy question: what is this Pokémon card actually worth? It resolves ~151,000 per-language printings into ~65,600 canonical cards and blends six disagreeing price sources into one market index per card — preserved as a live, browsable read-only archive.",
      "Next.js 15 + TypeScript on Turso (libSQL) and Cloudflare R2: 787 sets, 4.2M price snapshots, ~96,000 self-hosted images, 1,900+ sealed products — kept alive for $0/month.",
      {
        summary: "The data engine: identity, valuation, prediction.",
        details: [
          {
            summary:
              "One catalog from 151k printings — identity was the hard part.",
        details: [
          "No two sources name a card the same way, and the primary feeds don't cross-reference at all — naive name+set+number merging was ambiguous for over half of English cards. The canonicalizer layers evidence: naming grammar, set-cohort corroboration, and perceptual image hashing as tiebreaker, with a don't-guess policy below confidence thresholds.",
          {
            summary: "15,000+ Japanese cards matched to English identities.",
            details:
              "No shared IDs exist, so a twin-matcher fingerprints what survives translation: National Dex number + HP + attack signature (per-attack energy cost and damage) + illustrator. Powers the EN↔JP toggle on card pages.",
          },
          {
            summary: "Editions are first-class price series.",
            details:
              "1st Edition, Shadowless and Unlimited each carry their own history and images — values differ up to 40×. Valuation is edition-locked after a $15,000 1st-Edition pack was caught borrowing Unlimited prices; the fix moved its expected contents from $218 to $637.",
          },
          {
            summary: "Data hygiene as a permanent process.",
            details:
              "Rarity fields polluted with set fragments, a Mew named after its set, sealed boxes leaking into the card catalog, Near Mint priced under Moderately Played — each became a detection-and-repair script with validation gates, tested on a local replica before touching production.",
          },
        ],
      },
      {
        summary: "Every displayed price is a credibility-weighted market index.",
        details: [
          "Sources blend by trust — TCGplayer market above per-edition aggregates above depth-scaled eBay comps above PriceCharting — with sentinel scrubbing, a disagreement band, and a chorus override where independent sources outvote an outlier.",
          {
            summary: "Graded slabs valued per (company, grade).",
            details:
              "A PSA 10 isn't raw-price-times-a-multiplier: each grade is valued from its own sold comps through a four-level fallback ending in a population-calibrated curve for the card's era. Estimates are visibly labeled, and a monotonic smoother guarantees a PSA 9 never displays above a PSA 10.",
          },
          {
            summary: "Sealed expected value for 1,900+ products.",
            details:
              "EV = pack odds × the set's per-rarity card values, on pull rates researched from large community opening samples — measured for ~25 modern sets, era tables back to 1999, quirk handling for god packs, stamped promos and World Championship decks. Finding: modern English boxes “rip” at 28–46% of their sealed price.",
          },
          {
            summary: "The Charizard tax, measured.",
            details:
              "A residual model quantifies how much a character's cards systematically outprice comparable cards of the same set, rarity and type — a real, ranked premium across 1,015 species.",
          },
        ],
      },
      {
        summary: "Price-prediction research over 4.2M snapshots.",
        details: [
          "A local mirror (serverless SQL can't survive the aggregates), a weekly panel of ~15k series, and walk-forward evaluation with purged folds and always-up baselines. Honest results: naive momentum barely beats noise, set co-movement is 3× better, and a small ridge model on cohort features wins consistently.",
          {
            summary: "The audit that killed my best result.",
            details:
              "An adversarial pass over my own harness caught a condition-mixing bug manufacturing phantom ±50% moves behind an early “amazing” number. The evaluation was rebuilt leak-free and the claims shrank to what clean data supports.",
          },
        ],
          },
        ],
      },
      {
        summary: "A UI with collector-grade care.",
        details: [
          "The polish people remember, sitting on a pile of small correctness decisions:",
          {
            summary: "Rarity-aware 3D holo viewer.",
            details:
              "Cards tilt toward the cursor under stacked foil, sparkle and glare layers — CSS custom properties written in requestAnimationFrame, zero React re-renders. Eleven foil treatments mapped from the messy rarity vocabulary: Secret Rares shimmer rainbow, golds glow metallic, Commons just catch light. Touch and reduced-motion users get a clean static render.",
          },
          {
            summary: "Photorealistic graded-slab renders.",
            details:
              "Holdings display inside CSS recreations of PSA, BGS, CGC, SGC, TAG and ACE slabs — per-grader label typography, BGS subgrades, a genuinely scannable QR on TAG labels, and hash-seeded cert numbers (the barcode is a Rickroll).",
          },
          {
            summary: "A price chart that stays honest at 50 series per card.",
            details:
              "Raw and graded modes, per-edition series each with its own image, a range brush, multi-currency. Modeled values render blurred with low–high bands, and the trend chip sits behind evidence gates — minimum real observations, step-shape vetoes, staleness cutoffs — because one source ships reconstructed “histories” that produced confident nonsense until the gates existed.",
          },
          {
            summary: "Search that never disagrees with itself.",
            details:
              "The nav dropdown and search page share one engine — server FTS5 bm25 with trigram/Levenshtein fuzzy fallback — while species and illustrator suggestions come from a client-side static index. “4/102” finds the card.",
          },
          {
            summary: "A hundred collector-correct details.",
            details:
              "Language-correct card backs on flip, a lightbox that auto-uprights sideways BREAK cards, a hover magnifier, per-tile prices tinted by 7-day movement, species pages with evolution chains, ~440 illustrator galleries, a PSA cert lookup, and an editions field guide teaching Shadowless identification.",
          },
        ],
      },
      {
        summary: "Running it — and retiring it — were engineering problems too.",
        details: [
          {
            summary: "3GB of serverless SQL and a CI fleet that budgets itself.",
        details: [
          "libSQL decodes each response as one V8 string, so big reads are keyset-paginated; moving JSON filtering server-side into json_extract turned a 16-minute read into seconds; an overlapping writer once erased 10.5k rows mid-write and a silent timeout killed a nightly job for a week — every pipeline gained verification gates and single-writer discipline.",
          {
            summary: "Eight scheduled workflows under a self-written budget gate.",
            details:
              "Daily prices, premium recomputes, 3×/day eBay refresh prioritized by demand × staleness — all sharing a 2,000-minute monthly GitHub Actions budget behind a gate that measures month-to-date usage and refuses to start jobs past a ceiling.",
          },
          {
            summary: "A 96k-file image pipeline.",
            details:
              "Full-size and thumbnail WebP for the entire catalog, mirrored to Cloudflare R2 over the S3 API — resumable via manifest, immutable cache headers, served through a same-origin CDN proxy.",
          },
        ],
      },
      {
        summary: "Archived as a $0-forever site, on purpose.",
        details: [
          "The sunset became its own engineering task: accounts and every runtime write retired in code, scheduled jobs disabled, the stack refitted to verified free tiers — every failure mode is “stops working,” never “sends a bill.” Multi-currency runs off an ECB snapshot frozen at archive time, and the ops dashboard is public at /data as a transparency page.",
          {
            summary: "The Demo Trainer.",
            details:
              "The retired collection feature lives on as a built-in demo account: a sample portfolio valued once through the real pipeline and baked to static JSON — slab wall, P&L, and a portfolio chart whose acquisition dates were placed inside each card's real price history, so it shows genuine market movement.",
          },
        ],
          },
        ],
      },
    ],
    links: [
      { label: "Visit Archive", href: "https://poke-card-aggregate.netlify.app" },
    ],
  },
  // 3. Compassion Course
  {
    title: "Compassion Course Online",
    /* names prose actually uses; the chat linkifier matches these too */
    aliases: ["Compassion Course"],
    color: "#f4a261",
    tags: ["web", "software", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 4,
      role: "built and run solo for a client, in production",
      stack: ["React", "TypeScript", "Firebase (Auth, Firestore, Storage, Functions)", "Three.js", "ElevenLabs TTS"],
      scale: "50,000+ participants, 120+ countries, 20 languages; 52 scheduled lessons; a custom CMS the team publishes with",
      fit: ["full-stack product", "frontend", "backend", "client work"],
      hardest: "a four-mode lesson editor that writes edits back structure-preserving, with live safety checks and self-rebuilding narration",
    },
    images: CC_IMAGES,
    description: [
      "Built and run the platform for Thom Bond's Compassion Course — a year-long Nonviolent Communication program with 50,000+ participants across 120+ countries in 20 languages. Thom is the Director of Education at NYCNVC, a UN Civil Society Organization.",
      {
        summary: "React + Firebase platform with scheduled lessons and a 3D globe.",
        details:
          "React/TypeScript on Firebase (Auth, Firestore, Storage, Functions) with statically prerendered marketing pages, CI deploys, and passwordless email-link sign-in. The 52 weekly lessons unlock on a DST-correct schedule (noon New York, computed to exact UTC), and the home page carries an interactive Three.js globe of ~3,900 community locations.",
      },
      {
        summary: "A custom CMS — the team edits and publishes lessons themselves.",
        details: [
          "A full-screen lesson editor with four modes — visual WYSIWYG (the real lesson rendered in an iframe, prose editable in place, edits written back structure-preserving), raw HTML, member preview, and audio — plus live safety checks with one-click revert, autosave recovery, and named checkpoints. Publishing pushes the HTML to Cloud Storage with metadata in Firestore, all gated by role-based security rules; members read it through a sandboxed viewer on signed URLs.",
          {
            summary: "TTS narration is built into the CMS — one button, no engineer needed.",
            details:
              "Generating voiceovers is part of the editor itself, so the course team narrates lessons without any technical help: publishing auto-queues ElevenLabs jobs through a Firestore-triggered Cloud Function that streams progress back into the editor live. Clips are content-addressed (hashed from the section text), so identical sections share one file and only edited sections regenerate — they edit a lesson and its narration rebuilds itself.",
          },
        ],
      },
      {
        summary: "Team dashboard: Kanban, tasks, @mentions, shared whiteboards.",
        details:
          "A role-gated leadership area with teams, a drag-and-drop Kanban board with backlog and work items, @mention notifications, and Excalidraw whiteboards persisted to Firestore — the course staff's project-management home, inside the same site.",
      },
    ],
    links: [{ label: "Visit Site", href: "https://compassioncourse.org/" }],
  },
  // Lavoët
  {
    title: "Lavoët — Brand Revival & Pattern Studio",
    color: "#b8925a",
    tags: ["web", "software", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 3,
      role: "solo, for a client reviving the brand",
      stack: ["zero-dependency HTML/SVG/JS", "computer vision (registration, deconvolution, contour tracing)"],
      scale: "an interactive engraved catalogue plus a parametric pattern studio that refreshes in ~1 ms",
      fit: ["frontend", "design", "computer vision", "client work"],
      hardest: "rebuilding the 1930s monogram from one photograph, then rendering the whole repeating field as a single SVG pattern",
    },
    images: LAVOET_IMAGES,
    description: [
      "Brand development for Lavoët, a dormant Parisian trunk-maker founded 1869, being revived as a working brand: a catalogue-raisonné site plus production design software — each a single zero-dependency HTML file.",
      {
        summary:
          "An engraved catalogue you can open — every trunk is interactive inline SVG.",
        details:
          "Ivory paper, copperplate-engraving line work, zero image assets: every plate is drawn as inline SVG. Undo the brass clasps, buckles and locks on the four trunk plates and the lid lifts to reveal a packed interior — alongside working single-prong buckles, draggable wardrobe drawers, swaying hangers and a cursor-tilting brass plaque.",
      },
      {
        summary:
          "The monogram was rebuilt from a c.1930 trunk photo with computer vision.",
        details:
          "Template registration across ~24 motif instances, averaging, C4 symmetrization, Richardson–Lucy deconvolution and contour tracing rebuilt the original toile motif from a single photograph — then made it fully editable in the studio.",
      },
      {
        summary:
          "A parametric pattern studio designs and exports the house canvas.",
        details:
          "Drag-the-points shape editing with per-point roundness, swirl warps, 2–8-fold symmetry, two-tone 3D relief and lattice-aware logo placement — the repeating field renders as one SVG <pattern> (~190 DOM nodes at any canvas size, ~1 ms refresh, live while dragging). Exports seamless tiles and swatches as SVG/PNG with the full design config embedded, so any exported file re-opens to resume editing.",
      },
    ],
    links: [
      { label: "Visit Site", href: "https://hmohyud.github.io/lavoet/" },
      { label: "Pattern Studio", href: "https://hmohyud.github.io/lavoet/pattern-studio.html" },
      { label: "GitHub", href: "https://github.com/hmohyud/lavoet" },
    ],
  },
  // Soulful Expressions
  {
    title: "Soulful Expressions — Art Therapy",
    color: "#c67b5c",
    tags: ["web", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, for a client",
      stack: ["vanilla HTML/CSS/JS"],
      fit: ["frontend", "client work"],
      hardest: "making a framework-free page feel hands-on and experiential, like the therapy it describes",
    },
    images: SOUL_IMAGES,
    description: [
      "Designed and built the site for Soulful Expressions — Fatema Qutbuddin's art-therapy practice in Thane, offering one-on-one sessions and group workshops.",
      "Framework-free (vanilla HTML/CSS/JS) and fully interactive — a butterfly hero that follows the cursor, with hands-on touches throughout — so the site itself emulates the experiential nature of art therapy.",
    ],
    links: [
      { label: "Visit Site", href: "https://soulfulexpressions.in/" },
      { label: "GitHub", href: "https://github.com/hmohyud/soulful" },
    ],
  },
  // Environmental Data Globe
  {
    title: "Environmental Data Globe (Booth School of Business)",
    color: "#00d1ff",
    tags: ["software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 4,
      when: "Dec 2023 - Nov 2024",
      role: "software project lead on a university research team",
      stack: ["JavaScript", "Three.js", "Leaflet", "Python pipelines", "SQL on legacy servers"],
      scale: "200GB+ of environmental readings browsable in a browser",
      fit: ["data engineering", "data visualisation", "backend", "team lead"],
      hardest: "a recursive lat/long index with a summary at every depth, so 200GB stays fast at every zoom level",
    },
    images: GLOBE_IMAGES,
    description: [
      "Built the interactive globe behind Booth's “Corporate Footprints” research — 200GB+ of environmental readings explorable in the browser with JavaScript + Three.js, dynamic cluster filtering, and switchable palettes.",
      "Lost to the ages: it was an internal research tool and its deployment is no longer online — no screenshots survive, only the brand identity above and this write-up.",
      {
        summary: "A recursive lat/long index makes 200GB browsable in the browser.",
        details:
          "The dataset is subdivided into nested latitude/longitude cells, and every cell stores a summary of everything beneath it — the same pattern repeating at each depth. Any view reads a handful of small summaries instead of raw data, so zooming stays fast at every scale.",
      },
      {
        summary: "Geocoding and SQL wrangling on legacy university servers.",
        details:
          "Automated Python pipelines geocoded records and processed the compiled SQL data — partitioning, averaging, and subdividing clusters — against aging university database servers that predated modern tooling.",
      },
    ],
  },
  // 4. Aerospace Part Finder
  {
    title: "Aerospace Part Finder (Async Scraper + Local LLM)",
    color: "#c3ff7f",
    tags: ["software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 3,
      role: "solo, a personal convenience tool for a client's workflow",
      stack: ["Python asyncio", "Playwright", "trafilatura", "llama.cpp (local LLM)"],
      scale: "multi-strategy scraping with a local model reviewing results as structured JSON",
      fit: ["LLM integration", "automation", "backend"],
      hardest: "staying resilient to blocks and timeouts while a local model turns messy pages into structured JSON",
    },
    images: SPEC_IMAGES,
    description: [
      `Personal convenience tool to search suppliers for exact aerospace part numbers and officially documented equivalents.`,
      `Stack: async DuckDuckGo fallback, trafilatura (static) + Playwright (JS) scraping, concurrency, deduping, and a local llama.cpp model for structured JSON review.`,
      `Streams progress and tallies results; resilient to blocks with multiple fetch strategies and timeouts.`,
      `Since obsoleted by Google's AI search and Claude Cowork — effectively the same thing, done better.`,
    ],
    links: [
      {
        label: "Visit Site (UI only)",
        href: "https://hmohyud.github.io/azizproj/",
        note:
          "The hosted version is the front end only — the scraper and local LLM backend don't run on GitHub Pages.",
      },
      { label: "GitHub", href: "https://github.com/hmohyud/azizproj" },
    ],
  },
  // 5. Typometry
  {
    title: "Typometry — Absurdly Detailed Typing Stats",
    color: "#e2b714",
    tags: ["web", "software", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 4,
      role: "solo, made for fun",
      stack: ["React", "Vite", "Postgres triggers and views", "WebSockets", "hand-rolled SVG"],
      scale: "thirty-odd metrics per keystroke; global percentiles computed by database triggers; live multiplayer races",
      fit: ["frontend", "data", "backend", "real-time"],
      hardest: "computing global percentiles and records inside Postgres triggers instead of app code",
    },
    images: TYPO_IMAGES,
    description: [
      "Made purely for fun. Typing analysis that goes absurdly past WPM — every keystroke timed at sub-millisecond precision, then ranked against every other player. React + Vite, dark theme inspired by MonkeyType.",
      {
        summary: "Absurdly deep analysis — every keystroke becomes thirty-odd metrics.",
        details: [
          "performance.now() timing on every keystroke, backspaces included, computed into per-session and lifetime stats:",
          {
            summary: "Speed, rhythm, and fatigue metrics.",
            details:
              "Burst WPM over a sliding window, flow ratio, rhythm regularity, fatigue curves, post-error recovery penalty, capital- and punctuation-penalties, and a composite confidence score.",
          },
          {
            summary: "Distance-normalized bigram analysis.",
            details:
              "Every two-key sequence is timed and weighed against the physical key-travel distance on a coordinate model of the keyboard — surfacing “impressive” bigrams: long reaches typed unusually fast.",
          },
          {
            summary: "Hand-rolled SVG visualizations — no chart library.",
            details:
              "Keyboard heatmaps (speed or accuracy, comparable against global averages), a flow map with curved arrows tracing slow and fast key transitions, anatomical hand diagrams with nine-finger transition timings, per-row speed breakdowns, and a skill radar normalized to the global record.",
          },
          {
            summary: "13 typing archetypes + behavioral profiling.",
            details:
              "A metric cascade assigns archetypes — The Metronome, The Surgeon, The Steamroller, The Rattled, The Flow State… — alongside behavioral labels: momentum after errors (perfectionist → bulldozer), backspace efficiency (over-corrector, incomplete fixes), error clustering, even time-of-day performance patterns.",
          },
          {
            summary: "Global percentiles derived by database triggers, not app code.",
            details:
              "Clients submit raw keystroke streams under an anonymous, resettable UUID; Postgres triggers compute everything with incremental running statistics and histograms, exposed through ~17 read-only views for percentiles and records. A Cloudflare cron worker keeps the free-tier database awake.",
          },
        ],
      },
      {
        summary: "Realtime multiplayer racing over WebSockets.",
        details:
          "Live races run on broadcast + presence with zero database writes mid-race — ghost cursors, spectator mode, ready states, win streaks, and a strict mode that blocks wrong characters.",
      },
    ],
    links: [
      { label: "Visit Site", href: "https://hmohyud.github.io/typometry/" },
      // { label: "GitHub", href: "https://github.com/hmohyud/typometry" },
    ],
  },
  // 6. AutoLens
  {
    title: "AutoLens — Visual Vehicle ID (Expo)",
    color: "#6bff9d",
    tags: ["software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, contract",
      stack: ["Expo", "React Native", "server-side model inference"],
      fit: ["mobile", "AI/ML"],
      hardest: "on-device pre- and post-processing around a server-side model, with a results UI worth looking at",
    },
    image: `${PU}/projects/AUTO_STANDIN.png`,
    description: [
      "Mobile app that identifies cars from a photo (or live camera) and breaks down model trims, years, and key specs.",
      "Built with Expo + React Native; on-device pre/post-processing, server-side model inference, and a clean results UI.",
      "Preview it on your phone with Expo.",
    ],
    links: [
      {
        label: "Open preview (Expo Go installed)",
        href: "https://expo.dev/preview/update?message=Initial+commit%0A%0AGenerated+by+create-expo-app+3.4.2.&updateRuntimeVersion=1.0.0&createdAt=2025-06-30T12%3A23%3A53.766Z&slug=exp&projectId=bf485ddb-a27e-47b0-b8ba-444f4dbde301&group=ee75b629-263a-4bc1-aa43-7aa4a5313843",
      },
    ],
    qrData:
      "https://expo.dev/preview/update?message=Initial+commit%0A%0AGenerated+by+create-expo-app+3.4.2.&updateRuntimeVersion=1.0.0&createdAt=2025-06-30T12%3A23%3A53.766Z&slug=exp&projectId=bf485ddb-a27e-47b0-b8ba-444f4dbde301&group=ee75b629-263a-4bc1-aa43-7aa4a5313843",
  },
  // Simon - a talking bust
  {
    title: "Simon — a Talking Bust",
    color: "#e8c66a",
    tags: ["web", "software", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, built in a day for a friend",
      stack: ["Three.js physically-based materials", "Qwen 3.6 27B via GroqCloud"],
      fit: ["frontend", "LLM integration"],
      hardest: "convincing porcelain, stone and bronze in the browser, and a personality to match the pedestal",
    },
    minor: true,
    images: SIMON_IMAGES,
    description: [
      "A statue you can talk to — made for a friend who saw this site's point-cloud head, thought it was cool, and got one of his own spun up in a day. His sculpted head renders in-browser in physically-based materials (glazed porcelain, weathered stone, rusted bronze, or gold), with a chat box at its feet and a personality to match the pedestal: marble-proud, and unimpressed by the living.",
      "Replies come from Qwen 3.6 27B, served on-demand through GroqCloud.",
    ],
    links: [
      { label: "Talk to Simon", href: "https://hmohyud.github.io/simon_head/" },
    ],
  },
  // 7. Jar Jar Quote Bot
  {
    title: "Jar Jar Quote Bot (Twitter/X)",
    aliases: ["Jar Jar bot"],
    color: "#ff6b9d",
    tags: ["software"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo",
      stack: ["Python", "X API", "AI image generation"],
      fit: ["automation", "AI/ML"],
      hardest: "scheduling, retries, rate limits and moderation around a post that has to land every single day",
    },
    minor: true,
    images: JARJAR_IMAGES,
    description: [
      "Daily automation that scrapes a historical quote, translates it into Jar Jar Binks' Gungan speech, and posts it to X rendered over an AI-generated background (dodging the character limit) — with scheduling, retries, rate-limit handling, and moderation checks.",
    ],
    links: [{ label: "View on X", href: "https://x.com/JarJarbinksays" }],
  },
  // (AI Research & Model Tools was folded into SPIM above)
  // 10. Humrahe Khair
  {
    title: "Humrahe Khair — Together for Good",
    color: "#439184ff",
    tags: ["web"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, for a charity run by my extended family",
      stack: ["HTML/CSS/JS", "a nomination and registration pipeline"],
      fit: ["frontend", "client work"],
      hardest: "a nomination pipeline that non-technical organisers could run",
    },
    image: `${PU}/projects/CHARITY_1.jpg`,
    description: [
      `Designed and built the landing page and nomination/registration pipeline for Zahra Hasanaat's 2025 initiative—a charity run by my extended family.`,
      `The site promotes interfaith cooperation and highlights three pillars: free meals, Punji seed grants for small businesses, and vocational skills training.`,
    ],
    links: [
      { label: "Visit Site", href: "https://humrahekhair.zahrahasanaat.org/" },
    ],
  },
  // 11. Motām
  {
    title: "Motām — Poetry Collection",
    color: "#ffd166",
    tags: ["web", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 1,
      role: "solo, for my grandmother's poetry",
      stack: ["HTML/CSS"],
      fit: ["frontend", "design", "typography"],
      hardest: "typography and accessibility that stay out of the poems' way",
    },
    image: `${PU}/projects/MOTAM_1.jpg`,
    description: [
      `Designed and built a quiet website to present my grandmother's poetry collection across the years.`,
      `Typography-focused, accessible, and easy to maintain.`,
    ],
    links: [
      { label: "Visit Site", href: "https://fromearthtoeternity.com/" },
    ],
  },
  // 12. Join the Dots
  {
    title: "Join the Dots",
    color: "#9BAA8F",
    tags: ["web", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, for a community",
      stack: ["vanilla HTML/CSS/JS", "data-driven rendering", "full RTL support"],
      fit: ["frontend", "client work", "internationalisation"],
      hardest: "trilingual content with proper right-to-left Urdu throughout",
    },
    images: DOT_IMAGES,
    description: [
      "A trilingual (English, Hindi, Urdu) static site for a Nonviolent Communication community — event listings, a 12-week compassion course, curated resources with a canvas audio visualizer, and a community blog.",
      "Built without frameworks: vanilla HTML/CSS/JS, data-driven rendering, and full RTL support for Urdu.",
    ],
    links: [
      { label: "Visit Site", href: "https://jointhedots.info/" },
    ],
  },
  // Glo Pop
  {
    title: "Glo Pop — Product Landing Site",
    color: "#ff1493",
    tags: ["web", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 1,
      role: "solo, contract",
      stack: ["vanilla HTML/CSS/JS", "no build step"],
      fit: ["frontend", "client work", "brand sites"],
      hardest: "a hot-pink, high-energy identity that still loads instantly",
    },
    images: GLOWPOP_IMAGES,
    description: [
      "Contracted to design and build the landing site for Glo Pop, a collagen-lollipop brand marketed to a young female audience — the hot-pink, high-energy identity is built for exactly that demographic. Ingredient flip cards, a supplement comparison table, FAQ, and full social link-preview cards; vanilla HTML/CSS/JS, no framework, no build step.",
    ],
    links: [
      { label: "Visit Site", href: "https://hmohyud.github.io/glow-pop/" },
    ],
  },
  // Gorilla Greens (same client as Glo Pop)
  {
    title: "Gorilla Greens — Super Greens Landing Site",
    color: "#3FA75C",
    tags: ["web", "art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 1,
      role: "solo, contract, for the client behind Glo Pop",
      stack: ["vanilla HTML/CSS/JS", "no build step"],
      fit: ["frontend", "client work", "brand sites"],
      hardest: "the opposite identity to its sibling site, for the opposite demographic",
    },
    images: GG_IMAGES,
    description: [
      "A second brand build for the client behind Glo Pop — this time a dark, high-intensity identity for Gorilla Greens, a super-greens gummy pitched at men who train. Same framework-free approach (vanilla HTML/CSS/JS, no build step), tuned for a completely different demographic than its hot-pink sibling."
    ],
    links: [
      { label: "Visit Site", href: "https://hmohyud.github.io/greengummies/" },
    ],
  },
  // 13. High School Art Portfolio
  {
    title: "High School Art Portfolio",
    color: "#ff9ae0",
    tags: ["art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 1,
      role: "my own work",
      fit: ["art"],
    },
    images: ART_IMAGES,          // light webp thumbs for the carousel
    lightboxImages: ART_FULL,    // full-res originals for the zoom view
    description: [
      "Highlights, studies, and experiments from high school.",
      "College pieces are in progress—I haven't bundled them into a portfolio yet.",
    ],
    links: [{ label: "Open full gallery", href: `${PU}/art/` }],
  },
  // 14. Echoes of the Colorful Shadows
  {
    title: "The Echoes of the Colorful Shadows",
    color: "#4b7f96ff",
    tags: ["art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      role: "solo, a course final project",
      stack: ["early AI image tools", "print layout"],
      scale: "~2,000 generations curated to 130 panels in ~28 hours",
      fit: ["art", "generative AI"],
      hardest: "curating two thousand generations into a readable comic in a little over a day",
    },
    minor: true,
    images: COMIC_IMAGES,
    description: [
      "Final project for UChicago's Art & Machine Intelligence course — a comic made in ~28 hours at the dawn of AI image tools: ~2,000 generations curated down to 130 panels, cleaned up, lettered, and laid out for print.",
    ],
    links: [
      // { label: 'pdf', href: 'https://flowcode.com/p/2c3S4xwGF' }
      {
        label: "PDF",
        href: `${PU}/projects/Echoes_of_the_Colorful_Shadows_5x7.pdf`,
      },
    ],
    // Point QR at your canonical route so it never goes stale
    qrData: `${PU}/projects/QR_COMIC.png`,
  },
  // {
  //   title: 'Client Sites & Revamps',
  //   color: '#fe5757ff',
  //   image: '/images/sites-placeholder.jpg',
  //   description: [
  //     `Edited and refreshed sites for several people: accessibility passes, responsive layouts, perf budgets, and SEO fixes.`,
  //     `Typical stack: React/Vite/Next or lightweight static builds (Eleventy). Integrated forms, CMS where needed, and CI deploys.`,
  //   ]
  // },
  // 15. Pocket Sous Chef
  {
    title: "Pocket Sous Chef",
    color: "#b3fff2ff",
    tags: ["art"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 3,
      role: "solo, shipped on the App Store",
      stack: ["SwiftUI", "SwiftData", "OpenAI proxy (text + vision)"],
      fit: ["mobile", "AI/ML", "product"],
      hardest: "pantry photos to step-by-step recipes with no account and everything saved on the device",
    },
    images: COOK_IMAGES,
    description: [
      "iOS recipe assistant that turns pantry photos or typed ingredients into step-by-step recipes.",
      "Built with SwiftUI + SwiftData; OpenAI proxy for text + vision; saved recipes with nutrition.",
      "Privacy-minded: no account; recipes saved on device.",
    ],
    links: [
      {
        label: "App Store",
        href: "https://apps.apple.com/us/app/pocket-sous-chef/id6751048251",
      },
      {
        label: "Support",
        href: "https://hmohyud.github.io/pocketsouschef-support/",
      },
    ],
  },
  // 16. Educational Tech & Curriculum
  {
    title: "Educational Tech & Curriculum",
    color: "#ff8888",
    tags: ["other"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      when: "2016 - present",
      role: "instructor, tutor, curriculum author",
      stack: ["MIT App Inventor", "curriculum design"],
      scale: "1,000+ hours of tutoring; a bootcamp cohort of 30 veterans; 40 high-schoolers",
      fit: ["teaching", "mentoring", "communication"],
      hardest: "getting complete beginners to ship a working app before syntax got in the way",
    },
    images: TEACH_IMAGES,
    description: [
      "Nearly a decade of teaching programming and STEM — bootcamps, nonprofits, and 1,000+ hours of one-on-one tutoring.",
      {
        summary: "Code Platoon — coding bootcamp for veterans.",
        details: [
          "Assisted evenings-and-weekends bootcamp instruction for 30 veterans and active-duty service members transitioning into software careers.",
          {
            quote:
              "working with instructors and students to provide high-impact training to the underserved veteran community",
            by: "Chad Mowbray, Lead Instructor, Code Platoon",
          },
        ],
      },
      {
        summary: "Code Your Dreams — intro coding for inner-city students.",
        details: [
          "Taught a 7-week introductory coding course to 40 inner-city high-schoolers, and trained students in Youth-Initiated Mentoring — building relationships with supportive non-parental adults.",
          {
            summary: "Beginner-first curriculum built on MIT App Inventor.",
            details:
              "Developed block-based App Inventor examples and interactive modules so complete beginners could ship a working app before syntax ever got in the way.",
          },
        ],
      },
      {
        summary: "1,000+ hours of private tutoring since 2016.",
        details: [
          "Individualized instruction for 50+ middle-school, high-school, and college students across math, physics, chemistry, and essay writing.",
          {
            quote:
              "We have seen [our son] change from a child that disliked this subject to loving math and mastering math skills… I really can not say enough good things about him.",
            by: "parent of a three-year tutoring student",
          },
        ],
      },
    ],
  },
  // 17. IT Support & Deployment Automation
  {
    title: "IT Support & Deployment Automation",
    color: "#bbbbbb",
    tags: ["other"],
    /* chat ranking facts (build-chat-knowledge.mjs): effort is a 1-5 difficulty
       rating, fit the kinds of work the project is evidence for - tune freely */
    facts: {
      effort: 2,
      when: "2018 - 2021",
      role: "part-time technology support assistant",
      stack: ["OS imaging", "deployment scripting", "security setup"],
      scale: "100+ machines kept imaged and healthy",
      fit: ["sysadmin", "automation", "IT support"],
      hardest: "scripting the university's mandatory stack so a fresh machine came up configured by itself",
    },
    image: `${PU}/projects/IT_STANDIN.png`,
    description: [
      "Three years part-time as a Technology Support Assistant at the University of Chicago's Humanities Computing (2018–2021) — my first sysadmin work.",
      {
        summary: "Kept 100+ Humanities Division machines imaged and healthy.",
        details:
          "OS imaging, security setup, and software maintenance across the division's fleet, plus in-person configuration and troubleshooting for faculty and staff.",
      },
      {
        summary: "Automated OS + mandatory-application deployment.",
        details:
          "Scripted the university's required stack so a fresh machine came up configured without hand-installation — early, hands-on exposure to system maintenance and scripting that stuck.",
      },
    ],
  },
];

// Build a QR image URL (no extra deps)
const qrSrc = (data, size = 520) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    data
  )}`;

// Small webp preview for a full-res image URL, or null when none exists.
// /projects/<f>.<ext> -> /projects/thumbs/<f>.webp  (build-project-thumbs.mjs)
// /art/artworks/...   -> /art/thumbs/...webp        (build-art-assets.mjs)
const projThumbFor = (src) => {
  if (typeof src !== "string") return null;
  const m = src.match(/^(.*)\/projects\/([^/]+)\.(jpe?g|png|gif)$/i);
  if (m) return `${m[1]}/projects/thumbs/${m[2]}.webp`;
  if (src.includes("/art/artworks/"))
    return src
      .replace("/art/artworks/", "/art/thumbs/")
      .replace(/\.[^./]+$/, ".webp");
  return null;
};

/** Preload a list of URLs and return { ok, w, h } per src */
function usePreloaded(srcs) {
  const [meta, setMeta] = useState(() =>
    srcs.map(() => ({ ok: false, w: 0, h: 0 }))
  );

  // Key on the joined URLs, not array identity — inline arrays otherwise reset
  // the preload state (and reflash the spinner) on every parent re-render.
  const srcsKey = srcs.join("|");

  useEffect(() => {
    let alive = true;
    setMeta(srcs.map(() => ({ ok: false, w: 0, h: 0 })));

    srcs.forEach((src, i) => {
      if (!src) return;
      const img = new Image();

      const mark = () => {
        if (!alive) return;
        const w = img.naturalWidth || 0;
        const h = img.naturalHeight || 0;
        setMeta((prev) => {
          const next = [...prev];
          next[i] = { ok: true, w, h };
          return next;
        });
      };

      img.onload = mark;
      img.onerror = () => {
        /* leave as not ok */
      };
      img.decoding = "async";
      img.src = src;

      if (img.complete && img.naturalWidth > 0) {
        Promise.resolve().then(mark);
      }
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcsKey]);

  return meta; // [{ ok, w, h }]
}

// --------- RotatingImage (no overflow; uses <img> + object-fit: contain) ---------
// Progressive loading: the tiny webp thumb renders immediately; the full-res
// file is fetched only for the active slide (and the one on deck), then
// swapped in silently. Entries may be URL strings or { src, thumb } objects.
function RotatingImage({
  images = [],
  width = 280,
  height = 180,
  borderColor = "#4af",
  altBase = "project image",
  intervalMs = 5000,
  onClick,
  isVisible = true,
  compact = false, // minor-tier thumbnails: stay small beside the text on mobile
}) {
  const imagesKey = JSON.stringify(images);
  const slides = useMemo(
    () =>
      images.map((e) =>
        typeof e === "object" && e !== null
          ? { full: e.src, thumb: e.thumb || projThumbFor(e.src) }
          : { full: e, thumb: projThumbFor(e) }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesKey]
  );

  // Probe the cheapest available rendition to decide which slides exist.
  const probeSrcs = useMemo(
    () => slides.map((s) => s.thumb || s.full),
    [slides]
  );
  const meta = usePreloaded(probeSrcs);
  const ok = meta.map((m) => m.ok);

  const visibleIdxs = useMemo(
    () => slides.map((_, i) => (ok[i] ? i : -1)).filter((i) => i >= 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesKey, ok.join(",")]
  );

  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  // Which thumb-backed slides have their full-res file ready.
  const [fullLoaded, setFullLoaded] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setFullLoaded({}), [imagesKey]);

  // Fetch full resolution only for the active slide and the one on deck;
  // everything else stays on its lightweight thumb until its turn comes.
  useEffect(() => {
    if (!isVisible || !visibleIdxs.length) return;
    const n = visibleIdxs.length;
    [visibleIdxs[idx % n], visibleIdxs[(idx + 1) % n]].forEach((real) => {
      const s = slides[real];
      if (!s?.thumb || fullLoaded[real]) return; // thumbless slides already render full
      const img = new Image();
      img.decoding = "async";
      img.onload = () =>
        setFullLoaded((f) => (f[real] ? f : { ...f, [real]: true }));
      img.src = s.full;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, visibleIdxs, isVisible, slides]);

  // Keep idx in range if visible images change
  useEffect(() => {
    if (idx >= visibleIdxs.length) setIdx(0);
  }, [visibleIdxs.length, idx]);

  const next = () => setIdx((i) => (i + 1) % Math.max(visibleIdxs.length, 1));

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (visibleIdxs.length > 1 && isVisible) {
      timerRef.current = setInterval(next, intervalMs);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, visibleIdxs.length, isVisible]);

  const usable = visibleIdxs.length > 0;
  const activeReal = visibleIdxs.length ? visibleIdxs[idx] : -1;

  return (
    <div
      role="img"
      aria-label={`${altBase} — slideshow`}
      onMouseEnter={() => {
        setHovered(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }}
      onMouseLeave={() => {
        setHovered(false);
        if (!timerRef.current && visibleIdxs.length > 1 && isVisible) {
          timerRef.current = setInterval(next, intervalMs);
        }
      }}
      onClick={() => {
        if (usable) onClick?.(idx);
      }}
      className={`rot-img${compact ? " rot-img-sm" : ""}`}
      style={{
        position: "relative",
        "--w": `${width}px`,
        "--h": `${height}px`,
        cursor: usable ? "zoom-in" : "default",
        overflow: "hidden",
        backgroundColor: "#1a1a1a",
        borderRadius: 8,
        border: `1px solid ${borderColor}`,
        boxShadow: "0 0 10px rgba(255,255,255,0.04)",
      }}
    >
      {/* Images — thumb first, silently upgraded to full-res when fetched */}
      {slides.map((s, i) => {
        if (!ok[i]) return null;
        return (
          <img
            key={s.full + i}
            src={fullLoaded[i] || !s.thumb ? s.full : s.thumb}
            alt={`${altBase} ${i + 1}`}
            aria-hidden={activeReal !== i}
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              transition: "opacity 600ms ease",
              opacity: activeReal === i ? 1 : 0,
              display: "block",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        );
      })}

      {/* Loading spinner — visible until at least one image is ready */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          background: "#1a1a1a",
          opacity: usable ? 0 : 1,
          transition: "opacity 0.4s ease",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: "2.5px solid #333",
            borderTopColor: borderColor,
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>

      {/* Dot indicators with blocking strip */}
      {visibleIdxs.length > 1 && (
        <div
          aria-hidden="true"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()} // clicking the strip never opens lightbox
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 6,
            padding: "6px 10px", // a bit bigger hit area
            borderRadius: 999,
            background: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(4px)",
            opacity: hovered ? 0.9 : 0.4,
            transition: "opacity 180ms ease",
            cursor: "default", // not zoom-in over the strip
          }}
        >
          {visibleIdxs.map((realIdx, dotIndex) => {
            const isActive = dotIndex === idx;
            return (
              <button
                key={realIdx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation(); // extra safety
                  setIdx(dotIndex);
                }}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background: isActive ? borderColor : "rgba(255,255,255,0.55)",
                  opacity: isActive ? 1 : 0.7,
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// --------- emphasize: render **spans** in description strings ---------
// Card copy can wrap a phrase in ** ** to tint it with the project's accent.
function emphasize(text, color) {
  if (typeof text !== "string" || !text.includes("**")) return text;
  return text.split("**").map((seg, i) =>
    i % 2 ? (
      <span key={i} style={{ color, fontWeight: 600 }}>
        {seg}
      </span>
    ) : (
      seg
    )
  );
}

// --------- AspectRow: collapsible per-aspect detail ---------
// Collapsed: a one-line summary of the aspect. Expanded: the summary is
// replaced by the full details. Used for description entries shaped as
// { summary, details } — plain strings still render as ordinary <p>s.
function AspectRow({ summary, details, color, nested = false, style }) {
  const [open, setOpen] = useState(false);
  const paras = Array.isArray(details) ? details : [details];
  return (
    <div
      style={{
        border: "1px solid #2e2e2e",
        borderLeft: `${nested ? 2 : 3}px solid ${color}${nested ? "44" : "66"}`,
        borderRadius: 5,
        marginBottom: "0.35rem",
        background: open ? "rgba(255,255,255,0.02)" : "transparent",
        ...style,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 7,
          width: "100%",
          textAlign: "left",
          background: "transparent",
          border: "none",
          color: "#ddd",
          fontFamily: "monospace",
          fontSize: "0.82rem",
          lineHeight: 1.4,
          padding: "4px 8px",
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            color,
            flex: "0 0 auto",
            display: "inline-block",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        >
          ▸
        </span>
        {open ? (
          <span style={{ color: "#888", fontSize: "0.75rem", fontStyle: "italic" }}>
            hide details
          </span>
        ) : (
          <span>{summary}</span>
        )}
      </button>
      {open && (
        <div style={{ padding: "0 8px 6px 22px" }}>
          {paras.map((d, i) =>
            d && typeof d === "object" && d.quote ? (
              // attributed pull-quote inside an aspect's details
              <blockquote
                key={i}
                style={{
                  margin: i ? "0.45rem 0 0 0" : 0,
                  padding: "3px 10px",
                  borderLeft: `2px solid ${color}55`,
                  color: "#a9a9a9",
                  fontStyle: "italic",
                  fontSize: "0.8rem",
                  lineHeight: 1.5,
                }}
              >
                “{d.quote}”{" "}
                <span style={{ color: "#777", fontStyle: "normal" }}>
                  — {d.by}
                </span>
              </blockquote>
            ) : typeof d === "object" && d !== null ? (
              // nested accordion — a sub-aspect inside this aspect's details
              <AspectRow
                key={i}
                summary={d.summary}
                details={d.details}
                color={color}
                nested
                style={{ marginTop: i ? "0.4rem" : 0 }}
              />
            ) : (
              <p
                key={i}
                style={{
                  margin: i ? "0.4rem 0 0 0" : 0,
                  color: "#bbb",
                  fontSize: "0.82rem",
                  lineHeight: 1.5,
                }}
              >
                {d}
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [qrOpen, setQrOpen] = useState(null); // { title, data, color } | null
  const [lightbox, setLightbox] = useState(null); // { title, color, images, index }
  /* leaving the page forgets the card: "this one" means nothing elsewhere */
  useEffect(() => () => clearAttention(), []);

  /* Deep link from the chat: the head links project names to /projects#slug.
     Keyed on the location, not on mount, because clicking such a link while
     ALREADY on this page changes only the hash - the component never
     remounts, so a mount-only effect would do nothing (which is exactly what
     it did). location.key changes on every navigation, so re-clicking the
     same link re-runs it too.
     Cards carry lazy media, so the scroll repeats a few times while the
     layout settles rather than jumping once to a spot that is about to move,
     and the target flashes so it is obvious WHICH card was meant. */
  const location = useLocation();
  useEffect(() => {
    const id = decodeURIComponent((location.hash || "").replace("#", ""));
    if (!id) return undefined;
    /* a filter left on from earlier could be hiding the very card we were
       sent to - show everything so the target is definitely present */
    setActiveFilter("all");
    /* Two other things move this page: <ScrollToTop> on a route change (keyed
       on pathname, so a same-page hash click never triggers it) and the
       art-gallery restore below. An explicit deep link outranks a stale saved
       position, so drop it rather than letting the two fight - the gallery
       round-trip itself carries no hash and is untouched. */
    sessionStorage.removeItem("projects-scroll");

    let target = null;
    const timers = [0, 140, 420, 900].map((t) =>
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ block: "start", behavior: t ? "smooth" : "auto" });
        if (!target) {
          target = el;
          el.classList.remove("proj-flash");
          void el.offsetWidth; // restart the animation if it is still running
          el.classList.add("proj-flash");
        }
      }, t)
    );
    const done = setTimeout(() => {
      if (target) target.classList.remove("proj-flash");
    }, 2800);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(done);
      if (target) target.classList.remove("proj-flash");
    };
  }, [location.hash, location.key]);

  // Restore scroll position when returning from art gallery
  useEffect(() => {
    const saved = sessionStorage.getItem("projects-scroll");
    if (saved) {
      sessionStorage.removeItem("projects-scroll");
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
    }
  }, []);

  // Lock background scroll when any overlay is open
  useEffect(() => {
    const anyOpen = Boolean(qrOpen || lightbox);
    const prev = document.body.style.overflow;
    if (anyOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [qrOpen, lightbox]);

  // Keyboard: Esc closes overlays, arrows navigate lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightbox) setLightbox(null);
        else if (qrOpen) setQrOpen(null);
      }
      if (lightbox) {
        if (e.key === "ArrowRight")
          setLightbox((lb) => ({
            ...lb,
            index: (lb.index + 1) % lb.images.length,
          }));
        if (e.key === "ArrowLeft")
          setLightbox((lb) => ({
            ...lb,
            index: (lb.index - 1 + lb.images.length) % lb.images.length,
          }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qrOpen, lightbox]);

  const navButtonStyle = (color, side) => ({
    position: "absolute",
    [side]: 16,
    top: "50%",
    transform: "translateY(-50%)",
    width: 44,
    height: 44,
    padding: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "9999px",
    border: `1px solid ${color}`,
    background: "rgba(0,0,0,0.45)",
    color,
    cursor: "pointer",
    userSelect: "none",
    lineHeight: 1,
    fontSize: 22,
  });

  return (
    <div
      style={{
        padding: "0 2rem",
        maxWidth: "1000px",
        margin: "0 auto",
        fontFamily: "monospace",
        color: "#eaeaea",
        lineHeight: 1.6,
      }}
    >
      {/* image boxes: fixed width on desktop, full row width on small screens */}
      <style>{`
        .rot-img { width: var(--w); height: var(--h); flex: 0 0 var(--w); }
        @media (max-width: 640px) {
          .rot-img { width: 100%; flex: 1 1 100%; }
          /* minor-tier thumbs stay small and sit beside the text */
          .rot-img.rot-img-sm { width: 118px; flex: 0 0 118px; height: 80px; }
        }
        /* narrow screens centre the page: the heading, the filter pills, each
           project's row (its images and text block) and every project title;
           the body copy stays left-aligned inside its block, for reading */
        @media (max-width: 880px) {
          .proj-h1, .proj-section, .proj-title { text-align: center; }
          .proj-pills, .proj-row { justify-content: center; }
        }
        /* hover note on link pills (links with a note field) */
        .pill-tipwrap { position: relative; display: inline-block; }
        .pill-tip {
          position: absolute;
          bottom: calc(100% + 9px);
          left: 0;
          width: max-content;
          max-width: 270px;
          padding: 7px 10px;
          border: 1px solid #555;
          border-radius: 8px;
          background: rgba(12, 15, 21, 0.97);
          color: #cfd4e0;
          font-size: 0.72rem;
          line-height: 1.5;
          opacity: 0;
          transform: translateY(3px);
          pointer-events: none;
          transition: opacity 0.15s ease, transform 0.15s ease;
          z-index: 30;
        }
        .pill-tipwrap:hover .pill-tip,
        .pill-tipwrap:focus-within .pill-tip {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      <h1
        className="proj-h1"
        style={{
          fontSize: "2rem",
          marginBottom: "1.2rem",
          borderBottom: "1px solid #444",
          paddingBottom: "0.5rem",
        }}
      >
        Projects
      </h1>

      {/* Category filter pills */}
      <div
        className="proj-pills"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: "2rem",
        }}
      >
        {CATEGORIES.map(({ key, label }) => {
          const count =
            key === "all"
              ? projects.length
              : projects.filter((p) => p.tags?.includes(key)).length;
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              style={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                padding: "6px 14px",
                borderRadius: 999,
                border: `1px solid ${isActive ? "#eaeaea" : "#444"}`,
                background: isActive ? "rgba(234,234,234,0.1)" : "transparent",
                color: isActive ? "#eaeaea" : "#999",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = "#ccc";
                  e.currentTarget.style.borderColor = "#666";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = "#999";
                  e.currentTarget.style.borderColor = "#444";
                }
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {projects.filter((p) => !p.minor).map((proj, i) => {
        const images = proj.images || (proj.image ? [proj.image] : []);

        const isVisible =
          activeFilter === "all" || proj.tags?.includes(activeFilter);

        return (
          <div
            key={i}
            id={projectSlug(proj.title)}
            /* the chat's "tell me about this one": the card under the
               pointer, or last clicked, is what "this" means */
            onMouseEnter={() => noteAttention({ kind: "project", name: projectShortName(proj.title), slug: projectSlug(proj.title) })}
            onClickCapture={() => noteAttention({ kind: "project", name: projectShortName(proj.title), slug: projectSlug(proj.title) })}
            style={{
              scrollMarginTop: "84px",
              overflow: "hidden",
              opacity: isVisible ? 1 : 0,
              // generous cap — must exceed any card with every nested
              // accordion expanded (PokeCompare fully open passes 2400)
              maxHeight: isVisible ? 20000 : 0,
              marginBottom: isVisible ? "3rem" : 0,
              transform: isVisible ? "scale(1)" : "scale(0.97)",
              pointerEvents: isVisible ? "auto" : "none",
              transition: isVisible
                ? "opacity 0.35s ease 0.08s, transform 0.35s ease 0.08s, max-height 0.35s ease, margin-bottom 0.35s ease"
                : "opacity 0.2s ease, transform 0.2s ease, max-height 0.2s ease 0.15s, margin-bottom 0.2s ease 0.15s",
            }}
          >
          <div
            className="proj-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1.5rem",
              alignItems: "flex-start",
            }}
          >
            {/* Image box */}
            <RotatingImage
              images={images}
              borderColor={proj.color}
              altBase={`${proj.title} preview`}
              isVisible={isVisible}
              fit="height"
              onClick={(index) => {
                if (!images.length) return;
                const start = Math.min(index, (images.length || 1) - 1);
                setLightbox({
                  title: proj.title,
                  color: proj.color,
                  // carousel shows thumbs; the lightbox opens full-res when available
                  images: proj.lightboxImages || images,
                  index: start,
                });
              }}
            />

            {/* Text/content */}
            <div className="proj-text" style={{ flex: "1 1 500px" }}>
              <h2
                className="proj-title"
                style={{
                  color: proj.color,
                  margin: "0 0 0.5rem 0",
                  fontSize: "1.3rem",
                }}
              >
                {proj.title}
              </h2>
              {proj.description?.map((line, j) =>
                typeof line === "object" && line !== null ? (
                  <AspectRow
                    key={j}
                    summary={line.summary}
                    details={line.details}
                    color={proj.color}
                  />
                ) : (
                  <p key={j} style={{ margin: "0 0 0.75rem 0" }}>
                    {emphasize(line, proj.color)}
                  </p>
                )
              )}

              {proj.links?.length ? (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  {proj.links.map((l, k) => {
                    const pillStyle = {
                      display: "inline-block",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      border: `1px solid ${proj.color}`,
                      color: proj.color,
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      transition: "all .2s ease",
                    };
                    const hoverProps = {
                      onMouseEnter: (e) => {
                        e.currentTarget.style.backgroundColor = `${proj.color}22`;
                      },
                      onMouseLeave: (e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      },
                    };
                    // In-site pages navigate client-side, same tab
                    const pill = l.internal ? (
                      <Link to={l.href} style={pillStyle} {...hoverProps}>
                        {l.label}
                      </Link>
                    ) : (
                      <a
                        href={l.href}
                        {...(!/\/art\/?$/.test(l.href) && {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        })}
                        {...(/\/art\/?$/.test(l.href) && {
                          onClick: () => {
                            sessionStorage.setItem("projects-scroll", window.scrollY);
                          },
                        })}
                        style={pillStyle}
                        {...hoverProps}
                      >
                        {l.label}
                      </a>
                    );
                    // links with a note get a hover/focus tooltip
                    return l.note ? (
                      <span key={k} className="pill-tipwrap">
                        <div
                          className="pill-tip"
                          role="tooltip"
                          style={{ borderColor: `${proj.color}66` }}
                        >
                          {l.note}
                        </div>
                        {pill}
                      </span>
                    ) : (
                      <React.Fragment key={k}>{pill}</React.Fragment>
                    );
                  })}

                  {/* Fullscreen QR overlay trigger (only for items with qrData) */}
                  {proj.qrData ? (
                    <button
                      type="button"
                      onClick={() =>
                        setQrOpen({
                          title: proj.title,
                          data: proj.qrData,
                          color: proj.color,
                        })
                      }
                      style={{
                        padding: "6px 12px",
                        borderRadius: "999px",
                        border: `1px solid ${proj.color}`,
                        background: "transparent",
                        color: proj.color,
                        fontSize: "0.9rem",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${proj.color}22`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={qrOpen ? true : false}
                    >
                      {typeof proj.qrData === "string" &&
                        proj.qrData.toLowerCase().includes("expo.dev")
                        ? "Show QR (scan in Expo Go)"
                        : "Show QR"}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
          </div>
        );
      })}

      {/* ---- Smaller Pieces: compact tier for minor works ---- */}
      {(() => {
        const minors = projects.filter((p) => p.minor);
        const anyVisible = minors.some(
          (p) => activeFilter === "all" || p.tags?.includes(activeFilter)
        );
        if (!minors.length) return null;
        return (
          <div
            style={{
              overflow: "hidden",
              opacity: anyVisible ? 1 : 0,
              maxHeight: anyVisible ? 900 : 0,
              transition: "opacity 0.35s ease, max-height 0.35s ease",
            }}
          >
            <h2
              className="proj-section"
              style={{
                fontSize: "1.15rem",
                color: "#9aa3ad",
                borderBottom: "1px solid #333",
                paddingBottom: "0.4rem",
                margin: "0 0 1.4rem 0",
              }}
            >
              Smaller Pieces
            </h2>
            {minors.map((proj) => {
              const images = proj.images || (proj.image ? [proj.image] : []);
              const isVisible =
                activeFilter === "all" || proj.tags?.includes(activeFilter);
              if (!isVisible) return null;
              return (
                <div
                  key={proj.title}
                  id={projectSlug(proj.title)}
                  onMouseEnter={() => noteAttention({ kind: "project", name: projectShortName(proj.title), slug: projectSlug(proj.title) })}
                  onClickCapture={() => noteAttention({ kind: "project", name: projectShortName(proj.title), slug: projectSlug(proj.title) })}
                  className="proj-row proj-row-sm"
                  style={{
                    scrollMarginTop: "84px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "flex-start",
                    marginBottom: "1.6rem",
                  }}
                >
                  <RotatingImage
                    images={images}
                    width={170}
                    height={110}
                    compact
                    borderColor={proj.color}
                    altBase={`${proj.title} preview`}
                    isVisible={isVisible}
                    onClick={(index) => {
                      if (!images.length) return;
                      setLightbox({
                        title: proj.title,
                        color: proj.color,
                        images: proj.lightboxImages || images,
                        index: Math.min(index, images.length - 1),
                      });
                    }}
                  />
                  <div className="proj-text" style={{ flex: "1 1 150px" }}>
                    <h3
                      className="proj-title"
                      style={{
                        color: proj.color,
                        margin: "0 0 0.35rem 0",
                        fontSize: "1.02rem",
                      }}
                    >
                      {proj.title}
                    </h3>
                    {proj.description?.map((line, j) => (
                      <p
                        key={j}
                        style={{
                          margin: "0 0 0.5rem 0",
                          fontSize: "0.85rem",
                          lineHeight: 1.55,
                          color: "#c9c9c9",
                        }}
                      >
                        {line}
                      </p>
                    ))}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {proj.links?.map((l, k) => {
                        const pillStyle = {
                          padding: "4px 9px",
                          borderRadius: 999,
                          border: `1px solid ${proj.color}`,
                          color: proj.color,
                          textDecoration: "none",
                          fontSize: "0.8rem",
                        };
                        return l.internal ? (
                          <Link key={k} to={l.href} style={pillStyle}>
                            {l.label}
                          </Link>
                        ) : (
                          <a
                            key={k}
                            href={l.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={pillStyle}
                          >
                            {l.label}
                          </a>
                        );
                      })}
                      {proj.qrData ? (
                        <button
                          type="button"
                          onClick={() =>
                            setQrOpen({
                              title: proj.title,
                              data: proj.qrData,
                              color: proj.color,
                            })
                          }
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            border: `1px solid ${proj.color}`,
                            background: "transparent",
                            color: proj.color,
                            fontSize: "0.8rem",
                            cursor: "pointer",
                          }}
                        >
                          Show QR
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      <p style={{ fontStyle: "italic", marginTop: "2rem" }}>
        More is always in the works — visual experiments, creative tools, and
        exploratory AI included.
      </p>

      {/* Fullscreen QR overlay */}
      {qrOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="QR code overlay"
          onClick={() => {
            if (lightbox) setLightbox(null);
            else if (qrOpen) setQrOpen(null);
          }}
          // onClick={(e) => { if (e.target === e.currentTarget) setQrOpen(null); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "grid",
            placeItems: "center",
            padding: 24,
          }}
        >
          {(() => {
            const dataStr = String(qrOpen.data || "");
            const isExpo = dataStr.toLowerCase().includes("expo.dev");
            // const isInternal = dataStr.startsWith('/');

            const tipText = isExpo ? (
              <>
                Tip: Install <b>Expo Go</b> on iOS/Android, then scan this QR to
                open the preview.
              </>
            ) : (
              <>
                Tip: Open your phone's camera (or any QR app) and scan to open
                the page.
              </>
            );

            const imgAlt = isExpo
              ? "QR code for Expo preview"
              : "QR code for site link";
            const linkLabel = isExpo
              ? "Open preview in browser"
              : "Open site in new tab";
            // const copiedMsg = isExpo ? 'Preview URL copied to clipboard' : 'Link copied to clipboard';
            const imgLoc = isExpo
              ? qrOpen.data
              : `${PU}/projects/Echoes_of_the_Colorful_Shadows_5x7.pdf`;

            return (
              <div
                style={{
                  width: "min(92vw, 720px)",
                  borderRadius: 16,
                  border: `1px solid ${qrOpen.color || "#4af"}`,
                  background: "#0b0f14",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
                  padding: 20,
                  display: "grid",
                  gap: 14,
                  animation: "zoomIn .15s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <h3 style={{ margin: 0, color: "#eaeaea", fontSize: 18 }}>
                    Scan to open:{" "}
                    <span style={{ color: qrOpen.color }}>{qrOpen.title}</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => setQrOpen(null)}
                    style={{
                      border: "1px solid #2a2f3a",
                      background: "transparent",
                      color: "#bfc6d6",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#151a21";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    Close (Esc)
                  </button>
                </div>

                <p style={{ margin: 0, color: "#bfc6d6", fontSize: 14 }}>
                  {tipText}
                </p>

                <div
                  style={{
                    display: "grid",
                    placeItems: "center",
                    padding: "8px 0 2px",
                  }}
                >
                  <img
                    src={isExpo ? qrSrc(qrOpen.data, 720) : qrOpen.data}
                    alt={imgAlt}
                    style={{
                      width: "min(80vw, 70vh)",
                      height: "auto",
                      maxWidth: 560,
                      borderRadius: 12,
                      border: "1px solid #2a2f3a",
                      background: "#313944ff",
                      padding: 12,
                    }}
                  />
                  {/* {imgAlt} */}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <a
                    href={
                      isExpo
                        ? qrOpen.data
                        : `${PU}/projects/Echoes_of_the_Colorful_Shadows_5x7.pdf`
                    }
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      border: `1px solid ${qrOpen.color}`,
                      color: qrOpen.color,
                      textDecoration: "none",
                      background: "transparent",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${qrOpen.color}22`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {linkLabel}
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(imgLoc);
                        // alert(copiedMsg);
                      } catch {
                        prompt("Copy this URL:", imgLoc);
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      border: "1px solid #2a2f3a",
                      background: "transparent",
                      color: "#eaeaea",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#151a21";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            );
          })()}

          <style>{`
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes zoomIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      @media (prefers-reduced-motion: reduce) {
        * { transition-duration: 0s !important; animation-duration: 0s !important; }
      }
    `}</style>
        </div>
      )}

      {/* Lightbox overlay for image previews */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${lightbox.title} image viewer`}
          // onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          onClick={() => {
            if (lightbox) setLightbox(null);
            else if (qrOpen) setQrOpen(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 10000,
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            padding: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <h3 style={{ margin: 0, color: "#eaeaea", fontSize: 18 }}>
              {lightbox.title}
            </h3>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              style={{
                border: "1px solid #2a2f3a",
                background: "transparent",
                color: "#bfc6d6",
                borderRadius: 8,
                padding: "6px 10px",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#151a21";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              Close (Esc)
            </button>
          </div>

          <div
            style={{
              position: "relative",
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={lightbox.images[lightbox.index]}
              alt={`${lightbox.title} ${lightbox.index + 1}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "min(92vw, 1400px)",
                maxHeight: "min(80vh, 90vh)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                // borderRadius: 12,
                border: `1px solid ${lightbox.color}`,
                boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
              }}
              onError={() => {
                // If a lightbox image fails, advance to the next (avoid broken icon in overlay)
                setLightbox((lb) => {
                  const n = lb.images.length || 1;
                  return { ...lb, index: (lb.index + 1) % n };
                });
              }}
            />
            {lightbox.images.length > 1 && (
              <>
                <button
                  aria-label="Previous image"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((lb) => ({
                      ...lb,
                      index:
                        (lb.index - 1 + lb.images.length) % lb.images.length,
                    }));
                  }}
                  style={navButtonStyle(lightbox.color, "left")}
                >
                  ‹
                </button>
                <button
                  aria-label="Next image"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((lb) => ({
                      ...lb,
                      index: (lb.index + 1) % lb.images.length,
                    }));
                  }}
                  style={navButtonStyle(lightbox.color, "right")}
                >
                  ›
                </button>
              </>
            )}
          </div>

          <div style={{ textAlign: "center", color: "#bfc6d6", fontSize: 14 }}>
            {lightbox.images.length > 1
              ? `${lightbox.index + 1} / ${lightbox.images.length}`
              : ""}
          </div>
        </div>
      )}
    </div>
  );
}
